import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { SubscriptionBillingService } from '../../services/subscriptionBilling.service';
import { ISubscriptionInvoice, InvoiceStatus } from '../../interfaces/billing.interface';
import { useToast } from '../../context/ToastContext';

const formatAmount = (amount: number): string => `BDT ${amount.toFixed(2)}`;

const statusColor = (status: InvoiceStatus): 'default' | 'success' | 'warning' | 'error' => {
    switch (status) {
        case 'PAID':
            return 'success';
        case 'ISSUED':
            return 'warning';
        case 'CANCELLED':
            return 'error';
        default:
            return 'default';
    }
};

const trxIdFor = (invoice: ISubscriptionInvoice): string | null => {
    const paid = [...invoice.payments].reverse().find(p => p.gatewayTrxId);
    return paid ? paid.gatewayTrxId : null;
};

const BillingPage: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const { showApiError } = useToast();

    const [invoices, setInvoices] = useState<ISubscriptionInvoice[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [payingId, setPayingId] = useState<string | null>(null);

    const paymentParam = searchParams.get('payment');
    const [bannerVisible, setBannerVisible] = useState(!!paymentParam);

    const loadInvoices = useCallback(async () => {
        setLoading(true);
        setError(false);
        try {
            const page = await SubscriptionBillingService.getMyInvoices();
            setInvoices(page.content);
        } catch (err) {
            setError(true);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadInvoices();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setBannerVisible(!!paymentParam);
    }, [paymentParam]);

    const dismissBanner = () => {
        setBannerVisible(false);
        const next = new URLSearchParams(searchParams);
        next.delete('payment');
        setSearchParams(next, { replace: true });
    };

    const handlePay = async (invoice: ISubscriptionInvoice) => {
        setPayingId(invoice.id);
        try {
            const { redirectUrl } = await SubscriptionBillingService.payWithBkash(invoice.id);
            window.location.assign(redirectUrl);
        } catch (err) {
            showApiError(err, 'Failed to start bKash checkout. Please try again.');
        } finally {
            setPayingId(null);
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h5" sx={{ mb: 2 }}>
                Billing
            </Typography>

            {bannerVisible && paymentParam === 'processed' && (
                <Alert severity="info" onClose={dismissBanner} sx={{ mb: 2 }}>
                    Payment processed — check your invoice status below. It may take a moment to update.
                </Alert>
            )}
            {bannerVisible && paymentParam === 'failed' && (
                <Alert severity="error" onClose={dismissBanner} sx={{ mb: 2 }}>
                    Payment failed. Please try again.
                </Alert>
            )}

            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            )}

            {!loading && error && (
                <Alert
                    severity="error"
                    action={
                        <Button color="inherit" size="small" onClick={loadInvoices}>
                            Retry
                        </Button>
                    }
                >
                    Failed to load invoices. Please try again.
                </Alert>
            )}

            {!loading && !error && invoices.length === 0 && (
                <Typography color="text.secondary">No invoices yet.</Typography>
            )}

            {!loading && !error && invoices.length > 0 && (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Invoice #</TableCell>
                                <TableCell>Period</TableCell>
                                <TableCell>Amount</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Transaction ID</TableCell>
                                <TableCell align="right">Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {invoices.map(invoice => {
                                const trxId = trxIdFor(invoice);
                                return (
                                    <TableRow key={invoice.id}>
                                        <TableCell>{invoice.invoiceNumber}</TableCell>
                                        <TableCell>
                                            {invoice.periodStart} — {invoice.periodEnd}
                                        </TableCell>
                                        <TableCell>{formatAmount(invoice.amount)}</TableCell>
                                        <TableCell>
                                            <Chip label={invoice.status} color={statusColor(invoice.status)} size="small" />
                                        </TableCell>
                                        <TableCell>{trxId ?? '—'}</TableCell>
                                        <TableCell align="right">
                                            {invoice.status === 'ISSUED' && (
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    disabled={payingId === invoice.id}
                                                    onClick={() => handlePay(invoice)}
                                                    sx={{
                                                        bgcolor: '#E2136E',
                                                        '&:hover': { bgcolor: '#c50f60' },
                                                    }}
                                                >
                                                    {payingId === invoice.id ? 'Redirecting…' : 'Pay with bKash'}
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
};

export default BillingPage;
